/* eslint-disable no-param-reassign */

import { createSlice } from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';
import listSaga from './list';
import byIdSaga from './byId';
import createSaga, { ArticleCreateData } from './create';
import updateSaga, { ArticleUpdateData } from './update';
import deleteSaga from './delete';
import { BaseState, createCRUDModel, createCRUDStates } from '../creator';
import { RootState } from '../index';
import { PaginatedResponse } from '../types';
import { Tag } from '../tag';
import { Seo } from '../../types/seo';

export const moduleName = 'article';

export type ArticleStatus = 'published' | 'deactivated' | 'draft';
export const ArticleStatusTitle: Record<ArticleStatus, string> = {
  published: 'Published',
  deactivated: 'Deactivated',
  draft: 'Draft',
};

export interface ArticleFile {
  id: string;
  originalName: string;
  location: string;
  mimeType: string;
  createdAt: string;
}

export interface ArticleAuthorFields {
  authorImage: ArticleFile | null;
  authorName: string;
  authorDescription: string;
}

export interface Article extends ArticleAuthorFields {
  id: string;
  name: string;
  description: string;
  status: ArticleStatus;
  tags: Tag[];
  file: ArticleFile | null;
  video: ArticleFile | null;
  createdAt: string;
  updatedAt: string;
  slug: string;
  seo: Seo | null;
  isSeoReady: boolean;
}

export interface State {
  list: BaseState<PaginatedResponse<Article>>,
  byId: BaseState<Article>,
  create: BaseState<ArticleCreateData>,
  update: BaseState<ArticleUpdateData>,
  delete: BaseState<{ id: string }>
}

const models = ['list', 'byId', 'create', 'update', 'delete'];

export const defaultState: State = createCRUDStates<State>(models);

export const article = createSlice({
  name: moduleName,
  initialState: defaultState,
  reducers: createCRUDModel(models, moduleName),
});

export const { actions } = article;

export function useArticle(): State {
  return useSelector((state: RootState) => state[moduleName]);
}

export function* saga() {
  yield* listSaga();
  yield* byIdSaga();
  yield* createSaga();
  yield* updateSaga();
  yield* deleteSaga();
}
